import {
  HttpErrorResponse,
  HttpInterceptorFn,
  HttpResponse,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { catchError, tap, throwError } from 'rxjs';
import { CommunicateService } from '../services/communicate.service';
import { AdminNotifyService } from '../services/admin-notify.service';
import { STATUS_CODE } from '../Constants/statuscode.constant';
import { EssentialApiPathsService } from '../../hubspot/services/essential-api-paths.service';

/** True when running HubSpot CRM iframe flow — never bounce to Angular /login. */
function isHubSpotContext(
  router: Router,
  apiPaths: EssentialApiPathsService,
  requestUrl: string,
): boolean {
  if (apiPaths.isHubSpot) return true;

  const routerUrl = String(router.url || '');
  if (routerUrl.includes('/hubspot')) return true;

  try {
    if (typeof location !== 'undefined' && location.pathname.includes('/hubspot')) {
      return true;
    }
  } catch {
    // ignore
  }

  try {
    if (sessionStorage.getItem('signpl-hubspot-mode') === '1') return true;
  } catch {
    // ignore
  }

  // Any HubSpot backend API call
  if (/HubSpotPackets|\/HubSpot\//i.test(requestUrl)) return true;

  return false;
}

function bodyLooksUnauthorized(body: unknown): boolean {
  if (!body || typeof body !== 'object') return false;
  const b = body as Record<string, unknown>;
  const code = b['code'] ?? b['Code'] ?? b['statusCode'] ?? b['StatusCode'];
  return Number(code) === STATUS_CODE.UNAUTHORIZED;
}

export const authErrorHandleInterceptor: HttpInterceptorFn = (req, next) => {
  const communicate = inject(CommunicateService);
  const toastr = inject(ToastrService);
  const router = inject(Router);
  const apiPaths = inject(EssentialApiPathsService);
  const adminNotify = inject(AdminNotifyService);
  const authToken = localStorage.getItem('sp-token');

  const authReq = req.clone({
    setHeaders: {
      Authorization: `Bearer ${authToken}`,
      'Content-Type': 'application/json;',
    },
  });

  return next(authReq).pipe(
    tap((event) => {
      if (event instanceof HttpResponse) {
        try {
          adminNotify.handleApiSuccess(req.url, event.body, router.url);
        } catch {
          // never block the original API
        }
      }
      // Some SignPL APIs return HTTP 200 with { code: 401 } in the body
      if (!(event instanceof HttpResponse) || !bodyLooksUnauthorized(event.body)) {
        return;
      }
      if (isHubSpotContext(router, apiPaths, req.url)) {
        communicate.isLoaderLoad.next(false);
        console.warn(
          '[HubSpot] API body code 401 — skip login redirect',
          req.url,
        );
        return;
      }
      communicate.isLoaderLoad.next(false);
      toastr.error('Token has been expired. Please login again', '');
      router.navigate(['/login']);
    }),
    catchError((err: unknown) => {
      console.log(err);
      if (err instanceof HttpErrorResponse) {
        if (err.status === STATUS_CODE.UNAUTHORIZED) {
          communicate.isLoaderLoad.next(false);

          if (isHubSpotContext(router, apiPaths, req.url)) {
            console.warn('[HubSpot] HTTP 401 — skip login redirect', req.url);
          } else {
            toastr.error('Token has been expired. Please login again', '');
            router.navigate(['/login']);
          }
        } else {
          console.error('HTTP error:', err);
        }
      } else {
        console.error('An error occurred:', err);
      }
      return throwError(() => err);
    }),
  );
};
